const resources = {
  "en": {
    "closingHistory": {
      "title": "Closing summary",
      "description": "Review monthly close snapshots, yearly progression, and category concentration without changing the report logic.",
      "meta": "Monthly closing history",
      "valuation": {
        "estimatedTitle": "Closing summary uses explicit valuation",
        "estimatedDescription": "Monthly close totals are valued in {{currency}} from native balances, monthly flows, and current FX quotes.",
        "staleTitle": "Closing summary uses stale FX quotes",
        "staleDescription": "Monthly close totals are valued in {{currency}}, but some FX quotes are stale. Totals may lag until quotes refresh.",
        "degradedTitle": "Closing summary is partially degraded",
        "degradedDescription": "Monthly close totals are valued in {{currency}}, but some FX quotes are missing or failed. Totals may be incomplete until refresh succeeds.",
        "partialTitle": "Closing summary has partial valuation coverage",
        "partialDescription": "Monthly close totals are valued in {{currency}}, but some balances or flows cannot be valued with the current quote set. Totals reflect only the valuated subset."
      },
      "filters": {
        "summary": "{{year}} selected • {{month}}"
      },
      "annualOverview": "Annual overview",
      "annualOverviewDescription": "Select a month from the yearly registry to open the detailed close snapshot below.",
      "table": {
        "month": "Month",
        "start": "Start",
        "inflows": "Inflows",
        "outflows": "Outflows",
        "transfer": "Transfer",
        "close": "Close"
      },
      "details": {
        "title": "Details",
        "forPeriod": "Details for {{period}}",
        "description": "Inspect the selected month through the same calm structure each time."
      },
      "snapshot": {
        "title": "Monthly snapshot",
        "downloadPdf": "Download compact PDF"
      },
      "stats": {
        "startingBalance": "Starting balance",
        "inflows": "Inflows",
        "outflows": "Outflows",
        "transfers": "Transfers",
        "closingBalance": "Closing balance",
        "percentChange": "Percent change"
      },
      "categoryHighlights": "Category highlights",
      "categories": {
        "income": "Income",
        "expense": "Expenses",
        "transfer": "Transfers",
        "other": "Other categories"
      },
      "tags": {
        "entries_one": "{{count}} entry",
        "entries_other": "{{count}} entries",
        "more_one": "{{count}} more",
        "more_other": "{{count}} more"
      },
      "emptyState": {
        "month": "No activity recorded for this month",
        "transactions": "No transactions captured in this period"
      },
      "transactions": {
        "title": "Recent transactions",
        "itemTitle": "{{type}} — {{date}}",
        "itemDescription": "{{category}}",
        "showingFirst": "Showing the first {{count}} transactions for a concise view.",
        "types": {
          "income": "Income",
          "expense": "Expense",
          "transfer": "Transfer",
          "other": "Transaction"
        }
      },
      "pdf": {
        "fileNamePrefix": "closing-report",
        "fileName": "{{prefix}}-{{year}}-{{month}}.pdf",
        "period": "Period",
        "incomeBreakdown": "Income breakdown",
        "expenseBreakdown": "Expense breakdown",
        "transferBreakdown": "Transfers",
        "recentTransactions": "Recent transactions",
        "noneRecorded": "• None recorded",
        "transaction": "• {{type}} on {{date}} — {{category}}: {{amount}}",
        "categoryLine": "• {{category}} ({{count}}) — {{amount}}"
      },
      "messages": {
        "computeFailed": "Failed to compute closing report",
        "selectMonth": "Select a month to download its report"
      }
    }
  },
  "uk": {
    "closingHistory": {
      "title": "Підсумок закриття",
      "description": "Переглядай місячні зрізи закриття, річну динаміку та концентрацію категорій без зміни логіки звіту.",
      "meta": "Історія місячного закриття",
      "valuation": {
        "estimatedTitle": "Підсумок закриття використовує explicit valuation",
        "estimatedDescription": "Підсумки місячного закриття оцінюються в {{currency}} з native balances, місячних потоків і поточних FX-курсів.",
        "staleTitle": "Підсумок закриття використовує застарілі FX-курси",
        "staleDescription": "Підсумки місячного закриття оцінюються в {{currency}}, але частина FX-курсів застаріла. Підсумки можуть оновитися після refresh.",
        "degradedTitle": "Підсумок закриття частково деградований",
        "degradedDescription": "Підсумки місячного закриття оцінюються в {{currency}}, але частина FX-курсів відсутня або завершилася помилкою. Підсумки можуть бути неповними до успішного refresh.",
        "partialTitle": "Для підсумку закриття є лише часткове valuation coverage",
        "partialDescription": "Підсумки місячного закриття оцінюються в {{currency}}, але частину балансів або потоків не можна оцінити з поточним набором котирувань. Підсумки відображають лише оцінену частину."
      },
      "filters": {
        "summary": "{{year}} вибрано • {{month}}"
      },
      "annualOverview": "Річний огляд",
      "annualOverviewDescription": "Обери місяць у річному реєстрі, щоб відкрити детальний зріз закриття нижче.",
      "table": {
        "month": "Місяць",
        "start": "Початок",
        "inflows": "Надходження",
        "outflows": "Витрати",
        "transfer": "Перекази",
        "close": "Закриття"
      },
      "details": {
        "title": "Деталі",
        "forPeriod": "Деталі за {{period}}",
        "description": "Переглядай вибраний місяць у тій самій спокійній структурі щоразу."
      },
      "snapshot": {
        "title": "Місячний зріз",
        "downloadPdf": "Завантажити компактний PDF"
      },
      "stats": {
        "startingBalance": "Початковий баланс",
        "inflows": "Надходження",
        "outflows": "Витрати",
        "transfers": "Перекази",
        "closingBalance": "Кінцевий баланс",
        "percentChange": "Відсоткова зміна"
      },
      "categoryHighlights": "Ключові категорії",
      "categories": {
        "income": "Дохід",
        "expense": "Витрати",
        "transfer": "Перекази",
        "other": "Інші категорії"
      },
      "tags": {
        "entries_one": "{{count}} запис",
        "entries_few": "{{count}} записи",
        "entries_many": "{{count}} записів",
        "entries_other": "{{count}} записів",
        "more_one": "ще {{count}}",
        "more_few": "ще {{count}}",
        "more_many": "ще {{count}}",
        "more_other": "ще {{count}}"
      },
      "emptyState": {
        "month": "За цей місяць немає активності",
        "transactions": "У цьому періоді транзакції відсутні"
      },
      "transactions": {
        "title": "Останні транзакції",
        "itemTitle": "{{type}} — {{date}}",
        "itemDescription": "{{category}}",
        "showingFirst": "Показуємо перші {{count}} транзакцій для стислого перегляду.",
        "types": {
          "income": "Дохід",
          "expense": "Витрата",
          "transfer": "Переказ",
          "other": "Транзакція"
        }
      },
      "pdf": {
        "fileNamePrefix": "zvit-zakryttya",
        "fileName": "{{prefix}}-{{year}}-{{month}}.pdf",
        "period": "Період",
        "incomeBreakdown": "Розподіл доходів",
        "expenseBreakdown": "Розподіл витрат",
        "transferBreakdown": "Перекази",
        "recentTransactions": "Останні транзакції",
        "noneRecorded": "• Нічого не зафіксовано",
        "transaction": "• {{type}} за {{date}} — {{category}}: {{amount}}",
        "categoryLine": "• {{category}} ({{count}}) — {{amount}}"
      },
      "messages": {
        "computeFailed": "Не вдалося сформувати звіт про закриття",
        "selectMonth": "Оберіть місяць, щоб завантажити звіт"
      }
    }
  }
};

export default resources;
